import { useState, type FormEvent } from 'react'
import { useRequestLoginCode, useVerifyLoginCode } from '../api/me'
import { RequestFailed } from '../api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export function LoginPage() {
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [step, setStep] = useState<'email' | 'code'>('email')
  const request = useRequestLoginCode()
  const verify = useVerifyLoginCode()

  const onRequest = (e: FormEvent) => {
    e.preventDefault()
    request.mutate(email.trim(), { onSuccess: () => setStep('code') })
  }
  const onVerify = (e: FormEvent) => {
    e.preventDefault()
    verify.mutate({ email: email.trim(), code: code.trim() })
  }

  const error = (request.error ?? verify.error) as RequestFailed | null

  return (
    <main className="flex min-h-full items-center justify-center p-4">
      <div className="flex w-full max-w-sm flex-col gap-6 rounded-xl border bg-card p-6 sm:p-8">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold">Sign in to wattool</h1>
          <p className="text-sm text-muted-foreground">
            {step === 'email' ? 'We email you a one-time code. No password to remember.' : `We sent a 6-digit code to ${email}. It expires in 10 minutes.`}
          </p>
        </div>
        {step === 'email' ? (
          <form onSubmit={onRequest} noValidate className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="email">Work email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </div>
            <Button type="submit" disabled={!email.trim() || request.isPending}>
              {request.isPending ? 'Sending code' : 'Email me a code'}
            </Button>
          </form>
        ) : (
          <form onSubmit={onVerify} noValidate className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="code">Code</Label>
              <Input
                id="code"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                required
                autoFocus
                className="text-lg tracking-[0.3em] tabular-nums"
              />
            </div>
            <Button type="submit" disabled={code.length !== 6 || verify.isPending}>
              {verify.isPending ? 'Checking' : 'Sign in'}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setStep('email')}>
              Use a different email
            </Button>
          </form>
        )}
        {error && (
          <p role="alert" className="rounded-lg bg-destructive-soft p-3 text-sm text-destructive">
            {error.message}
          </p>
        )}
      </div>
    </main>
  )
}
